import { Link, useLocation, useTranslator } from "gemi/client";
import { useState } from "react";
import { trackEvent } from "../utils/analytics";
import { useAuthModal } from "./AuthModalV2";
import { setAuthIntent } from "./authIntent";
import { LanguageToggleV2 } from "./LanguageToggleV2";
import { useLandingSession } from "./useLandingSession";
import { font } from "./_shared";

const LINKS = [
  { href: "/", key: "home" },
  { href: "/pricing", key: "pricing" },
  { href: "/about", key: "about" },
  { href: "/contact", key: "contact" },
] as const;

function PersonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="5.2" r="2.6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M3.2 13c0-2.4 2.1-4 4.8-4s4.8 1.6 4.8 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SiteNavV2() {
  const t = useTranslator("SiteNavV2");
  const { pathname } = useLocation();
  const { user, loading } = useLandingSession();
  const { open: openAuth } = useAuthModal();
  const [open, setOpen] = useState(false);

  const isAuthenticated = !loading && Boolean(user);

  // Plain "Log in" is a direct sign-in, not a studio CTA: it targets chat, the default post-auth
  // surface. Writing "chat" explicitly (rather than only clearing the intent) also overrides a stale
  // ai-studio intent left by a studio CTA the visitor opened and dismissed earlier this session. A
  // plain login starts a fresh chat intent, so any pending HeroPrompt composer payload is dropped by
  // design (a nav login is not a composer submit).
  const loginAuth = () => {
    setAuthIntent({ target: "chat" });
    openAuth("signin");
  };

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  return (
    <div className="relative z-20" style={font.body}>
      {/* Desktop */}
      <nav className="mx-auto hidden w-full max-w-[1440px] items-center justify-between px-16 py-6 lg:flex">
        <Link href="/" className="flex flex-none items-center">
          <img
            src="/v2/logo-lockup.svg"
            alt="Folio AI"
            className="block h-[26px] w-auto"
          />
        </Link>
        <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-11">
          {LINKS.map((l) => (
            <Link
              key={l.key}
              href={l.href}
              onClick={
                l.key === "pricing"
                  ? () =>
                      trackEvent({
                        event: "pricing_click",
                        params: { from: "pricing_link" },
                      })
                  : undefined
              }
              className="text-[16px] font-medium tracking-[-0.1px] no-underline"
              style={{ color: isActive(l.href) ? "#343139" : "#86848A" }}
            >
              {t(l.key)}
            </Link>
          ))}
        </div>
        <div className="flex flex-none items-center gap-3">
          <LanguageToggleV2 />
          {loading ? (
            // /v2 is public-cached with no auth middleware, so the session read starts
            // as loading with no user. Show a neutral placeholder (not "Log in") so a
            // signed-in visitor never sees/clicks the wrong CTA before it resolves.
            <div
              aria-hidden
              className="h-[42px] w-[132px] animate-pulse rounded-full bg-[rgba(25,24,24,0.08)]"
            />
          ) : isAuthenticated ? (
            <Link
              href="/app/redirect"
              onClick={() =>
                setAuthIntent({ target: "ai-studio" }, user?.publicId ?? null)
              }
              className="flex items-center gap-2 rounded-full bg-[#19180F] px-[18px] py-[11px] text-[15px] font-medium text-[#F7F7F2] no-underline"
            >
              <PersonIcon />
              {t("access_profile")}
            </Link>
          ) : (
            <button
              type="button"
              onClick={loginAuth}
              className="flex items-center gap-2 rounded-full bg-[#19180F] px-[18px] py-[11px] text-[15px] font-medium text-[#F7F7F2]"
            >
              <PersonIcon />
              {t("log_in")}
            </button>
          )}
        </div>
      </nav>

      {/* Mobile */}
      <div className="lg:hidden">
        <nav className="flex w-full items-center justify-between px-5 py-[18px]">
          <Link href="/" className="flex items-center">
            <img
              src="/v2/logo-lockup.svg"
              alt="Folio AI"
              className="block h-6 w-auto"
            />
          </Link>
          <button
            type="button"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex size-11 flex-col items-center justify-center gap-[5px] rounded-xl border border-[rgba(25,24,24,0.14)] bg-[#F3F1EC]"
          >
            <span className="block h-0.5 w-[18px] rounded-sm bg-[#343139]" />
            <span className="block h-0.5 w-[18px] rounded-sm bg-[#343139]" />
            <span className="block h-0.5 w-[18px] rounded-sm bg-[#343139]" />
          </button>
        </nav>
        {open && (
          <div className="flex flex-col items-center gap-7 border-t border-[rgba(25,24,24,0.08)] bg-white px-5 pb-14 pt-10">
            {LINKS.map((l) => (
              <Link
                key={l.key}
                href={l.href}
                onClick={() => {
                  setOpen(false);
                  if (l.key === "pricing") {
                    trackEvent({
                      event: "pricing_click",
                      params: { from: "pricing_link" },
                    });
                  }
                }}
                className="text-[28px] no-underline"
                style={{
                  ...font.display,
                  color: isActive(l.href) ? "#343139" : "#86848A",
                }}
              >
                {t(l.key)}
              </Link>
            ))}
            {!loading &&
              (isAuthenticated ? (
                <Link
                  href="/app/redirect"
                  onClick={() => {
                    setAuthIntent(
                      { target: "ai-studio" },
                      user?.publicId ?? null,
                    );
                    setOpen(false);
                  }}
                  className="mt-3 flex items-center gap-2 rounded-full bg-[#19180F] px-6 py-3 text-[15px] font-medium text-[#F7F7F2] no-underline"
                >
                  <PersonIcon />
                  {t("access_profile")}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    loginAuth();
                  }}
                  className="mt-3 flex items-center gap-2 rounded-full bg-[#19180F] px-6 py-3 text-[15px] font-medium text-[#F7F7F2]"
                >
                  <PersonIcon />
                  {t("log_in")}
                </button>
              ))}
            <LanguageToggleV2 onSelect={() => setOpen(false)} />
          </div>
        )}
      </div>
    </div>
  );
}
